import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminManager from '../services/AdminManager';
import { useTheme } from '../theme/ThemeContext';


function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
}


function getCursorForHandle(handle) {
    const map = {
        nw: 'nwse-resize', se: 'nwse-resize',
        ne: 'nesw-resize', sw: 'nesw-resize',
        n: 'ns-resize', s: 'ns-resize',
        w: 'ew-resize', e: 'ew-resize',
        move: 'move',
    };
    return map[handle] || 'crosshair';
}

function AnnotationCanvas({ imageUrl, boxes, selectedIndex, onBoxesChange, onSelectBox, zoom }) {
    const wrapperRef = useRef(null);
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const [imgRendered, setImgRendered] = useState({ w: 0, h: 0 });
    const [drawing, setDrawing] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [activeHandle, setActiveHandle] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [cursor, setCursor] = useState('crosshair');

    const getNormPos = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
        return {
            x: clamp((e.clientX - rect.left) / rect.width, 0, 1),
            y: clamp((e.clientY - rect.top) / rect.height, 0, 1),
        };
    }, []);

    const HANDLE_NORM = 0.015;

    const getHandleForBox = useCallback((bx, px, py) => {
        if (!bx || !bx.visible) return null;
        const left = bx.x_center - bx.width / 2;
        const top = bx.y_center - bx.height / 2;
        const right = bx.x_center + bx.width / 2;
        const bottom = bx.y_center + bx.height / 2;
        const midX = bx.x_center;
        const midY = bx.y_center;
        const hr = HANDLE_NORM;

        const corners = [
            { name: 'nw', cx: left, cy: top },
            { name: 'ne', cx: right, cy: top },
            { name: 'sw', cx: left, cy: bottom },
            { name: 'se', cx: right, cy: bottom },
            { name: 'n', cx: midX, cy: top },
            { name: 's', cx: midX, cy: bottom },
            { name: 'w', cx: left, cy: midY },
            { name: 'e', cx: right, cy: midY },
        ];
        for (const c of corners) {
            if (Math.abs(px - c.cx) <= hr && Math.abs(py - c.cy) <= hr) {
                return c.name;
            }
        }
        if (px >= left && px <= right && py >= top && py <= bottom) {
            return 'move';
        }
        return null;
    }, []);

    const findBoxAtPoint = useCallback((px, py) => {
        if (!boxes) return { index: -1, handle: null };
        // Check selected box first for handle priority
        if (selectedIndex >= 0 && selectedIndex < boxes.length) {
            const handle = getHandleForBox(boxes[selectedIndex], px, py);
            if (handle) return { index: selectedIndex, handle };
        }
        // Then check all boxes for containment (reverse order = topmost first)
        for (let i = boxes.length - 1; i >= 0; i--) {
            const handle = getHandleForBox(boxes[i], px, py);
            if (handle) return { index: i, handle };
        }
        return { index: -1, handle: null };
    }, [boxes, selectedIndex, getHandleForBox]);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (!imgRendered.w || !imgRendered.h) return;

        const drawW = imgRendered.w;
        const drawH = imgRendered.h;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = drawW * dpr;
        canvas.height = drawH * dpr;

        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, drawW, drawH);

        const visibleBoxes = (boxes || []).filter(b => b && b.visible);
        if (visibleBoxes.length === 0) return;

        // Dim outside all boxes
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(0, 0, drawW, drawH);
        for (const bx of visibleBoxes) {
            const x = (bx.x_center - bx.width / 2) * drawW;
            const y = (bx.y_center - bx.height / 2) * drawH;
            const w = bx.width * drawW;
            const h = bx.height * drawH;
            ctx.clearRect(x, y, w, h);
        }

        // Draw all boxes
        for (let i = 0; i < (boxes || []).length; i++) {
            const bx = boxes[i];
            if (!bx || !bx.visible) continue;

            const isSelected = i === selectedIndex;
            const x = (bx.x_center - bx.width / 2) * drawW;
            const y = (bx.y_center - bx.height / 2) * drawH;
            const w = bx.width * drawW;
            const h = bx.height * drawH;

            // Box stroke
            ctx.strokeStyle = isSelected ? '#00ff88' : '#00cc66';
            ctx.lineWidth = isSelected ? 3 : 1.5;
            ctx.setLineDash(isSelected ? [] : [4, 3]);
            ctx.strokeRect(x, y, w, h);
            ctx.setLineDash([]);

            // Light fill
            ctx.fillStyle = isSelected ? 'rgba(0, 255, 136, 0.08)' : 'rgba(0, 200, 100, 0.04)';
            ctx.fillRect(x, y, w, h);

            // Handles (only for selected)
            if (isSelected) {
                const hs = 8;
                const handles = [
                    [x, y], [x + w, y], [x, y + h], [x + w, y + h],
                    [x + w / 2, y], [x + w / 2, y + h],
                    [x, y + h / 2], [x + w, y + h / 2],
                ];
                ctx.fillStyle = '#fff';
                ctx.strokeStyle = '#00ff88';
                ctx.lineWidth = 1.5;
                for (const [hx, hy] of handles) {
                    ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs);
                    ctx.strokeRect(hx - hs / 2, hy - hs / 2, hs, hs);
                }
            }

            // Label
            ctx.fillStyle = isSelected ? 'rgba(0, 255, 136, 0.9)' : 'rgba(0, 200, 100, 0.7)';
            ctx.font = isSelected ? 'bold 12px monospace' : '11px monospace';
            const label = `#${i + 1} ${(bx.width * 100).toFixed(0)}%x${(bx.height * 100).toFixed(0)}%`;
            ctx.fillText(label, x + 4, y - 6 > 14 ? y - 6 : y + 14);
        }
    }, [boxes, selectedIndex, imgRendered]);

    useEffect(() => { draw(); }, [draw]);

    const handleImgLoad = useCallback((e) => {
        const img = e.target;
        requestAnimationFrame(() => {
            const w = img.offsetWidth;
            const h = img.offsetHeight;
            if (w > 0 && h > 0) {
                setImgRendered({ w, h });
            }
        });
    }, []);

    useEffect(() => {
        setImgRendered({ w: 0, h: 0 });
        let attempts = 0;
        const maxAttempts = 20;
        const pollInterval = setInterval(() => {
            attempts++;
            const img = imgRef.current;
            if (img && img.offsetWidth > 0 && img.offsetHeight > 0) {
                setImgRendered({ w: img.offsetWidth, h: img.offsetHeight });
                clearInterval(pollInterval);
            } else if (attempts >= maxAttempts) {
                clearInterval(pollInterval);
            }
        }, 100);
        return () => clearInterval(pollInterval);
    }, [imageUrl]);

    useEffect(() => {
        const handleResize = () => {
            const img = imgRef.current;
            if (img && img.offsetWidth > 0) {
                setImgRendered({ w: img.offsetWidth, h: img.offsetHeight });
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleMouseDown = (e) => {
        e.preventDefault();
        const pos = getNormPos(e);
        const { index, handle } = findBoxAtPoint(pos.x, pos.y);

        if (index >= 0 && handle === 'move') {
            onSelectBox(index);
            setActiveHandle('move');
            const bx = boxes[index];
            const left = bx.x_center - bx.width / 2;
            const top = bx.y_center - bx.height / 2;
            setDragOffset({ x: pos.x - left, y: pos.y - top });
        } else if (index >= 0 && handle) {
            onSelectBox(index);
            setActiveHandle(handle);
            setDragStart(pos);
        } else {
            // Draw new box
            onSelectBox(-1);
            setDrawing(true);
            setDragStart(pos);
        }
    };

    const handleMouseMove = (e) => {
        const pos = getNormPos(e);

        if (drawing && dragStart) {
            const x1 = Math.min(dragStart.x, pos.x);
            const y1 = Math.min(dragStart.y, pos.y);
            const x2 = Math.max(dragStart.x, pos.x);
            const y2 = Math.max(dragStart.y, pos.y);
            const w = x2 - x1;
            const h = y2 - y1;
            if (w > 0.01 && h > 0.01) {
                const newBox = { x_center: (x1 + x2) / 2, y_center: (y1 + y2) / 2, width: w, height: h, visible: true };
                // If we started drawing a new box, add it; otherwise update the last added
                const currentBoxes = boxes || [];
                if (dragStart._newBoxAdded) {
                    const updated = [...currentBoxes];
                    updated[updated.length - 1] = newBox;
                    onBoxesChange(updated);
                } else {
                    dragStart._newBoxAdded = true;
                    onBoxesChange([...currentBoxes, newBox]);
                    onSelectBox(currentBoxes.length);
                }
            }
            return;
        }

        const selectedBox = (selectedIndex >= 0 && boxes) ? boxes[selectedIndex] : null;

        if (activeHandle === 'move' && selectedBox) {
            let newLeft = pos.x - dragOffset.x;
            let newTop = pos.y - dragOffset.y;
            newLeft = clamp(newLeft, 0, 1 - selectedBox.width);
            newTop = clamp(newTop, 0, 1 - selectedBox.height);
            const updated = [...boxes];
            updated[selectedIndex] = {
                ...selectedBox,
                x_center: newLeft + selectedBox.width / 2,
                y_center: newTop + selectedBox.height / 2,
            };
            onBoxesChange(updated);
            return;
        }

        if (activeHandle && selectedBox) {
            let left = selectedBox.x_center - selectedBox.width / 2;
            let top = selectedBox.y_center - selectedBox.height / 2;
            let right = selectedBox.x_center + selectedBox.width / 2;
            let bottom = selectedBox.y_center + selectedBox.height / 2;

            if (activeHandle.includes('e')) { right = clamp(pos.x, left + 0.02, 1); }
            if (activeHandle.includes('w')) { left = clamp(pos.x, 0, right - 0.02); }
            if (activeHandle.includes('s')) { bottom = clamp(pos.y, top + 0.02, 1); }
            if (activeHandle.includes('n')) { top = clamp(pos.y, 0, bottom - 0.02); }

            const updated = [...boxes];
            updated[selectedIndex] = {
                x_center: (left + right) / 2,
                y_center: (top + bottom) / 2,
                width: right - left,
                height: bottom - top,
                visible: true,
            };
            onBoxesChange(updated);
            return;
        }

        // Update cursor
        const { handle } = findBoxAtPoint(pos.x, pos.y);
        setCursor(handle ? getCursorForHandle(handle) : 'crosshair');
    };

    const handleMouseUp = () => {
        setDrawing(false);
        setActiveHandle(null);
        setDragStart(null);
        setDragOffset({ x: 0, y: 0 });
    };

    return (
        <div
            ref={wrapperRef}
            style={{
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#111',
            }}
        >
            <div style={{
                position: 'relative',
                transform: `scale(${zoom})`,
                transformOrigin: 'center center',
                transition: 'transform 0.1s ease',
            }}>
                <img
                    ref={imgRef}
                    src={imageUrl}
                    alt="annotate"
                    onLoad={handleImgLoad}
                    style={{
                        display: 'block',
                        maxWidth: '85vw',
                        maxHeight: '78vh',
                        userSelect: 'none',
                        pointerEvents: 'none',
                    }}
                    draggable={false}
                />
                <canvas
                    ref={canvasRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        cursor: cursor,
                    }}
                />
            </div>
        </div>
    );
}

function ImageThumbnailStrip({ images, bboxes, currentIndex, onSelect, theme }) {
    const stripRef = useRef(null);

    useEffect(() => {
        if (stripRef.current && stripRef.current.children[currentIndex]) {
            stripRef.current.children[currentIndex].scrollIntoView({
                behavior: 'smooth', block: 'nearest', inline: 'center',
            });
        }
    }, [currentIndex]);

    return (
        <div
            ref={stripRef}
            style={{
                display: 'flex',
                gap: 6,
                overflowX: 'auto',
                padding: '8px 0',
                scrollbarWidth: 'thin',
            }}
        >
            {images.map((img, idx) => {
                const entry = bboxes[img.filename];
                const hasBbox = Array.isArray(entry) && entry.some(b => b && b.visible);
                const boxCount = Array.isArray(entry) ? entry.filter(b => b && b.visible).length : 0;
                const isActive = idx === currentIndex;
                return (
                    <div
                        key={img.filename}
                        onClick={() => onSelect(idx)}
                        style={{
                            minWidth: 64,
                            width: 64,
                            height: 64,
                            borderRadius: 6,
                            overflow: 'hidden',
                            border: isActive
                                ? '3px solid #00ff88'
                                : hasBbox
                                    ? '2px solid rgba(0,255,136,0.4)'
                                    : `2px solid ${theme.border}`,
                            cursor: 'pointer',
                            opacity: isActive ? 1 : 0.7,
                            transition: 'all 0.15s',
                            position: 'relative',
                            flexShrink: 0,
                        }}
                    >
                        <img
                            src={img.url}
                            alt={img.filename}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            draggable={false}
                        />
                        {hasBbox && (
                            <div style={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                minWidth: 14,
                                height: 14,
                                borderRadius: 7,
                                background: '#00ff88',
                                border: '1px solid #000',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 9,
                                fontWeight: 700,
                                color: '#000',
                                padding: '0 3px',
                            }}>
                                {boxCount}
                            </div>
                        )}
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'rgba(0,0,0,0.6)',
                            padding: '1px 3px',
                            fontSize: 8,
                            color: '#ccc',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                        }}>
                            {idx + 1}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function AnnotationScreen() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();

    const [images, setImages] = useState([]);
    const [bboxes, setBboxes] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedBoxIndex, setSelectedBoxIndex] = useState(-1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [productName, setProductName] = useState('');
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);

    const saveTimeout = useRef(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [imgData, bboxData, productData] = await Promise.all([
                AdminManager.getProductImages(productId),
                AdminManager.getProductBBoxes(productId).catch(() => ({ bboxes: {} })),
                AdminManager.getProduct(productId).catch(() => null),
            ]);
            setImages(imgData.images || []);
            setBboxes(bboxData.bboxes || {});
            if (productData) {
                setProductName(`${productData.brand_name} ${productData.product_name}`);
            }
        } catch (e) {
            console.error('Failed to load annotation data:', e);
        }
        setLoading(false);
    }, [productId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const autoSave = useCallback((newBboxes) => {
        setUnsavedChanges(true);
        if (saveTimeout.current) clearTimeout(saveTimeout.current);
        saveTimeout.current = setTimeout(async () => {
            setSaving(true);
            try {
                await AdminManager.saveProductBBoxes(productId, newBboxes);
                setUnsavedChanges(false);
                setLastSaved(new Date());
            } catch (e) {
                console.error('Auto-save failed:', e);
            }
            setSaving(false);
        }, 800);
    }, [productId]);

    const handleBoxesChange = useCallback((newBoxes) => {
        const filename = images[currentIndex]?.filename;
        if (!filename) return;
        const updated = { ...bboxes, [filename]: newBoxes };
        setBboxes(updated);
        autoSave(updated);
    }, [images, currentIndex, bboxes, autoSave]);

    const handleDeleteBox = useCallback(() => {
        const filename = images[currentIndex]?.filename;
        if (!filename) return;
        const currentBoxes = bboxes[filename] || [];
        if (selectedBoxIndex >= 0 && selectedBoxIndex < currentBoxes.length) {
            const updated = { ...bboxes };
            const newBoxes = [...currentBoxes];
            newBoxes.splice(selectedBoxIndex, 1);
            updated[filename] = newBoxes;
            setBboxes(updated);
            setSelectedBoxIndex(-1);
            autoSave(updated);
        } else if (currentBoxes.length > 0) {
            const updated = { ...bboxes, [filename]: [] };
            setBboxes(updated);
            setSelectedBoxIndex(-1);
            autoSave(updated);
        }
    }, [images, currentIndex, bboxes, selectedBoxIndex, autoSave]);

    const handleRejectImage = useCallback(async () => {
        const filename = images[currentIndex]?.filename;
        if (!filename) return;
        try {
            await AdminManager.tagProductImages(productId, { [filename]: 'rejected' });
        } catch (e) {
            console.error('Failed to reject image:', e);
        }
        const newImages = images.filter((_, i) => i !== currentIndex);
        setImages(newImages);
        if (currentIndex >= newImages.length) {
            setCurrentIndex(Math.max(0, newImages.length - 1));
        }
        setSelectedBoxIndex(-1);
    }, [images, currentIndex, productId]);

    const goNext = useCallback(() => {
        setCurrentIndex(i => Math.min(i + 1, images.length - 1));
        setSelectedBoxIndex(-1);
    }, [images.length]);

    const goPrev = useCallback(() => {
        setCurrentIndex(i => Math.max(i - 1, 0));
        setSelectedBoxIndex(-1);
    }, []);

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
                e.preventDefault();
                goNext();
            } else if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
                e.preventDefault();
                goPrev();
            } else if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                handleDeleteBox();
            } else if (e.key === 'x' || e.key === 'X') {
                e.preventDefault();
                handleRejectImage();
            } else if (e.key === '+' || e.key === '=') {
                setZoom(z => Math.min(z + 0.2, 3));
            } else if (e.key === '-') {
                setZoom(z => Math.max(z - 0.2, 0.4));
            } else if (e.key === '0') {
                setZoom(1);
            } else if (e.key === 'Escape') {
                navigate(-1);
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [goNext, goPrev, handleDeleteBox, handleRejectImage, navigate]);

    const handleAutoAnnotate = async () => {
        setSaving(true);
        try {
            await AdminManager.autoAnnotateProduct(productId);
            const bboxData = await AdminManager.getProductBBoxes(productId);
            setBboxes(bboxData.bboxes || {});
            setLastSaved(new Date());
        } catch (e) {
            console.error('Auto-annotate failed:', e);
        }
        setSaving(false);
    };

    const currentImage = images[currentIndex];
    const currentBoxes = currentImage ? (bboxes[currentImage.filename] || []) : [];
    const annotatedCount = Object.values(bboxes).filter(arr => Array.isArray(arr) && arr.some(b => b && b.visible)).length;

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0a0a0a', color: '#fff' }}>
                Loading annotation data...
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            background: '#0a0a0a',
            color: '#e0e0e0',
            overflow: 'hidden',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}>
            {/* Top bar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 20px',
                background: '#1a1a1a',
                borderBottom: '1px solid #333',
                flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            background: 'none',
                            border: '1px solid #444',
                            borderRadius: 6,
                            padding: '6px 12px',
                            color: '#ccc',
                            cursor: 'pointer',
                            fontSize: 13,
                        }}
                    >
                        &larr; Back
                    </button>
                    <div>
                        <span style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>
                            {productName || 'Product Annotation'}
                        </span>
                        <span style={{ fontSize: 12, color: '#888', marginLeft: 12 }}>
                            {currentIndex + 1} / {images.length}
                        </span>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 11, color: '#666' }}>
                        {annotatedCount}/{images.length} annotated
                        {currentBoxes.length > 0 && (
                            <span style={{ marginLeft: 8, color: '#00ff88' }}>
                                ({currentBoxes.filter(b => b?.visible).length} boxes)
                            </span>
                        )}
                    </span>
                    <div style={{
                        width: 100,
                        height: 4,
                        borderRadius: 2,
                        background: '#333',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            width: `${images.length ? (annotatedCount / images.length) * 100 : 0}%`,
                            height: '100%',
                            background: '#00ff88',
                            transition: 'width 0.3s',
                        }} />
                    </div>

                    {saving && (
                        <span style={{ fontSize: 11, color: '#ffa500' }}>Saving...</span>
                    )}
                    {!saving && unsavedChanges && (
                        <span style={{ fontSize: 11, color: '#ffa500' }}>Unsaved</span>
                    )}
                    {!saving && !unsavedChanges && lastSaved && (
                        <span style={{ fontSize: 11, color: '#00ff88' }}>Saved</span>
                    )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                        onClick={handleAutoAnnotate}
                        style={{
                            background: 'rgba(156, 39, 176, 0.15)',
                            color: '#ce93d8',
                            border: '1px solid rgba(156, 39, 176, 0.3)',
                            borderRadius: 6,
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        Auto-Annotate (Gemini)
                    </button>
                    <button
                        onClick={handleDeleteBox}
                        disabled={currentBoxes.length === 0}
                        style={{
                            background: currentBoxes.length > 0 ? 'rgba(255, 68, 68, 0.1)' : 'rgba(100,100,100,0.1)',
                            color: currentBoxes.length > 0 ? '#ff6666' : '#666',
                            border: `1px solid ${currentBoxes.length > 0 ? 'rgba(255,68,68,0.3)' : 'rgba(100,100,100,0.2)'}`,
                            borderRadius: 6,
                            padding: '6px 12px',
                            fontSize: 12,
                            cursor: currentBoxes.length > 0 ? 'pointer' : 'default',
                        }}
                    >
                        {selectedBoxIndex >= 0 ? `Delete #${selectedBoxIndex + 1}` : `Delete All (${currentBoxes.length})`}
                    </button>
                    <button
                        onClick={handleRejectImage}
                        style={{
                            background: 'rgba(255, 68, 68, 0.15)',
                            color: '#ff4444',
                            border: '1px solid rgba(255,68,68,0.4)',
                            borderRadius: 6,
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        Reject Image (X)
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
                        <button onClick={() => setZoom(z => Math.max(z - 0.2, 0.4))} style={{ background: '#222', border: '1px solid #444', borderRadius: 4, color: '#ccc', width: 28, height: 28, cursor: 'pointer', fontSize: 14 }}>-</button>
                        <span style={{ fontSize: 11, color: '#888', minWidth: 36, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
                        <button onClick={() => setZoom(z => Math.min(z + 0.2, 3))} style={{ background: '#222', border: '1px solid #444', borderRadius: 4, color: '#ccc', width: 28, height: 28, cursor: 'pointer', fontSize: 14 }}>+</button>
                        <button onClick={() => setZoom(1)} style={{ background: '#222', border: '1px solid #444', borderRadius: 4, color: '#888', padding: '4px 8px', cursor: 'pointer', fontSize: 10, marginLeft: 4 }}>Reset</button>
                    </div>
                </div>
            </div>

            {/* Main area */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Canvas area */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {currentImage ? (
                        <AnnotationCanvas
                            imageUrl={currentImage.url}
                            boxes={currentBoxes}
                            selectedIndex={selectedBoxIndex}
                            onBoxesChange={handleBoxesChange}
                            onSelectBox={setSelectedBoxIndex}
                            zoom={zoom}
                        />
                    ) : (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                            No images to annotate
                        </div>
                    )}

                    {/* Navigation arrows */}
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: 20,
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                    }}>
                        <button
                            onClick={goPrev}
                            disabled={currentIndex === 0}
                            style={{
                                background: 'rgba(0,0,0,0.6)',
                                border: '1px solid #444',
                                borderRadius: '50%',
                                width: 40,
                                height: 40,
                                color: currentIndex === 0 ? '#444' : '#fff',
                                fontSize: 20,
                                cursor: currentIndex === 0 ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            &lsaquo;
                        </button>
                    </div>
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        right: 20,
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                    }}>
                        <button
                            onClick={goNext}
                            disabled={currentIndex >= images.length - 1}
                            style={{
                                background: 'rgba(0,0,0,0.6)',
                                border: '1px solid #444',
                                borderRadius: '50%',
                                width: 40,
                                height: 40,
                                color: currentIndex >= images.length - 1 ? '#444' : '#fff',
                                fontSize: 20,
                                cursor: currentIndex >= images.length - 1 ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            &rsaquo;
                        </button>
                    </div>
                </div>
            </div>

            {/* Bottom thumbnail strip */}
            <div style={{
                padding: '8px 16px',
                background: '#1a1a1a',
                borderTop: '1px solid #333',
                flexShrink: 0,
            }}>
                <ImageThumbnailStrip
                    images={images}
                    bboxes={bboxes}
                    currentIndex={currentIndex}
                    onSelect={setCurrentIndex}
                    theme={theme}
                />
            </div>

            {/* Keyboard shortcuts hint */}
            <div style={{
                padding: '4px 16px 8px',
                background: '#1a1a1a',
                display: 'flex',
                justifyContent: 'center',
                gap: 20,
                flexShrink: 0,
            }}>
                {[
                    ['←/→', 'Navigate'],
                    ['Drag', 'Draw box'],
                    ['Handles', 'Resize'],
                    ['Inside box', 'Move'],
                    ['Del', 'Remove box'],
                    ['X', 'Reject image'],
                    ['+/-', 'Zoom'],
                    ['Esc', 'Exit'],
                ].map(([key, desc]) => (
                    <span key={key} style={{ fontSize: 10, color: '#555' }}>
                        <kbd style={{ background: '#222', border: '1px solid #444', borderRadius: 3, padding: '1px 4px', fontSize: 9, color: '#888' }}>{key}</kbd>{' '}
                        {desc}
                    </span>
                ))}
            </div>
        </div>
    );
}
